// File: /Users/alex/PilotMode/GlobalCommand-Center/FrontStack/src/Pages/LandingPage/ModernHeroSection.js

import React from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Container,
  Paper,
  Chip,
  LinearProgress,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import { stats } from './data';

const ModernHeroSection = ({ onGetStartedClick }) => {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.4,
        }}
      />

      {/* Floating Gradient Orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          right: "8%",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "float 6s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px)" },
            "50%": { transform: "translateY(-30px)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          left: "5%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.25) 0%, transparent 70%)",
          filter: "blur(80px)",
          animation: "float 8s ease-in-out infinite",
          animationDelay: "1s",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 10, py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            {/* Premium Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                px: 2.5,
                py: 1,
                borderRadius: "100px",
                border: "1px solid rgba(255,255,255,0.3)",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#10b981",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }}
              />
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                🚀 Trusted by 10,000+ Teams Worldwide
              </Typography>
            </Box>

            {/* Main Headline with Gradient Text */}
            <Typography
              variant="h1"
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem" },
                lineHeight: 1.1,
                mb: 3,
                letterSpacing: "-2px",
              }}
            >
              Work Smarter,
              <br />
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                Not Harder
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: "linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)",
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: "rgba(255,255,255,0.95)",
                fontWeight: 400,
                lineHeight: 1.7,
                mb: 2,
                maxWidth: 550,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
              }}
            >
              Transform your workflow with the{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#fbbf24" }}>
                #1 AI-powered
              </Box>{" "}
              task management platform. Boost team productivity by 10x.
            </Typography>

            {/* Key Benefits */}
            <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
              {[
                { icon: <SpeedIcon />, text: "10x Faster" },
                { icon: <SecurityIcon />, text: "Bank-Level Security" },
                { icon: <TrendingUpIcon />, text: "94% Success Rate" },
              ].map((benefit, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#fff",
                  }}
                >
                  {React.cloneElement(benefit.icon, {
                    sx: { fontSize: 20, color: "#fbbf24" },
                  })}
                  <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                    {benefit.text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA Buttons */}
            <Box sx={{ display: "flex", gap: 2.5, mb: 4, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                onClick={onGetStartedClick}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: "linear-gradient(135deg, #fff 0%, #f8f8f8 100%)",
                  color: "#667eea",
                  px: 5,
                  py: 2,
                  fontSize: "18px",
                  fontWeight: 800,
                  borderRadius: "14px",
                  textTransform: "none",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                  border: "2px solid rgba(255,255,255,0.3)",
                  "&:hover": {
                    background: "#fff",
                    transform: "translateY(-3px)",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PlayCircleOutlineIcon />}
                sx={{
                  borderColor: "rgba(255,255,255,0.6)",
                  color: "#fff",
                  px: 4,
                  py: 2,
                  fontSize: "18px",
                  fontWeight: 700,
                  borderRadius: "14px",
                  textTransform: "none",
                  borderWidth: "2px",
                  backdropFilter: "blur(10px)",
                  background: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    borderColor: "#fff",
                    background: "rgba(255,255,255,0.2)",
                    transform: "translateY(-3px)",
                    borderWidth: "2px",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Watch Demo
              </Button>
            </Box>

            {/* Social Proof */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
                mb: 3,
              }}
            >
              <AvatarGroup max={5} sx={{ "& .MuiAvatar-root": { border: "2px solid #667eea" } }}>
                <Avatar sx={{ bgcolor: "#10b981", width: 40, height: 40 }}>J</Avatar>
                <Avatar sx={{ bgcolor: "#f59e0b", width: 40, height: 40 }}>S</Avatar>
                <Avatar sx={{ bgcolor: "#ec4899", width: 40, height: 40 }}>M</Avatar>
                <Avatar sx={{ bgcolor: "#8b5cf6", width: 40, height: 40 }}>A</Avatar>
                <Avatar sx={{ bgcolor: "#06b6d4", width: 40, height: 40 }}>R</Avatar>
              </AvatarGroup>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} sx={{ color: "#fbbf24", fontSize: 18 }} />
                  ))}
                </Box>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Rated 4.9/5 from 10,000+ reviews
                </Typography>
              </Box>
            </Box>

            {/* Trust Indicators */}
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
              {[
                "✓ Free 14-day trial",
                "✓ No credit card required",
                "✓ Cancel anytime",
              ].map((text, index) => (
                <Typography
                  key={index}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Right Side - Enhanced Stats Dashboard */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: "28px",
                background: "rgba(255,255,255,0.98)",
                backdropFilter: "blur(30px)",
                boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
                border: "1px solid rgba(255,255,255,0.5)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative Gradient */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 200,
                  height: 200,
                  background: "radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%)",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                }}
              />

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Live Platform Metrics
                  </Typography>
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "11px",
                      animation: "pulse 2s ease-in-out infinite",
                    }}
                  />
                </Box>

                {/* Stats Grid */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                  {stats.map((stat, index) => (
                    <Grid item xs={6} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "20px",
                          background: `linear-gradient(135deg, ${stat.color}08 0%, ${stat.color}03 100%)`,
                          border: `2px solid ${stat.color}20`,
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-8px) scale(1.02)",
                            boxShadow: `0 16px 40px ${stat.color}30`,
                            border: `2px solid ${stat.color}50`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "16px",
                            background: `linear-gradient(135deg, ${stat.color}25 0%, ${stat.color}15 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          {React.cloneElement(stat.icon, {
                            sx: { color: stat.color, fontSize: 28 },
                          })}
                        </Box>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 900,
                            color: "#1f2937",
                            mb: 0.5,
                            fontSize: "2.2rem",
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Live Activity Feed */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#6b7280",
                      mb: 2,
                      fontSize: "13px",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    🔥 Trending Tasks Today
                  </Typography>
                  {[
                    { task: "Q4 Marketing Strategy", progress: 88, team: "Marketing", color: "#ec4899" },
                    { task: "Product Roadmap 2026", progress: 67, team: "Product", color: "#8b5cf6" },
                    { task: "Client Onboarding Sprint", progress: 95, team: "Sales", color: "#10b981" },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        borderRadius: "16px",
                        background: "#fff",
                        border: "2px solid #f3f4f6",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          border: `2px solid ${item.color}40`,
                          transform: "translateX(5px)",
                          boxShadow: `0 8px 24px ${item.color}15`,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: "#1f2937",
                            fontSize: "15px",
                          }}
                        >
                          {item.task}
                        </Typography>
                        <Chip
                          label={item.team}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: "12px",
                            fontWeight: 700,
                            background: `${item.color}20`,
                            color: item.color,
                            border: `1px solid ${item.color}30`,
                          }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={item.progress}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#e5e7eb",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}dd 100%)`,
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            color: item.color,
                            fontSize: "14px",
                            minWidth: 42,
                          }}
                        >
                          {item.progress}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Animated Scroll Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: 40,
          left: "50%",
          transform: "translateX(-50%)",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          animation: "fadeInUp 2s ease-out",
          "@keyframes fadeInUp": {
            "0%": { opacity: 0, transform: "translate(-50%, 20px)" },
            "100%": { opacity: 1, transform: "translate(-50%, 0)" },
          },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 52,
            border: "3px solid rgba(255,255,255,0.6)",
            borderRadius: 20,
            display: "flex",
            justifyContent: "center",
            padding: "6px 0",
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 10,
              backgroundColor: "#fbbf24",
              borderRadius: 3,
              animation: "scrollDown 2.5s ease-in-out infinite",
              "@keyframes scrollDown": {
                "0%": { transform: "translateY(0)", opacity: 0 },
                "40%": { opacity: 1 },
                "80%": { transform: "translateY(22px)", opacity: 0 },
                "100%": { opacity: 0 },
              },
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.8)",
            mt: 1.5,
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          Explore More
        </Typography>
      </Box>
    </Box>
  );
};

export default ModernHeroSection;